import * as React from 'react'
import { SubscriptionType } from '../../gen/types'
import { TEAM_MONTHLY_SUBSCRIPTION_TYPE } from '../../subscription-types'
import type { TeamSubscriptionType } from '../../subscription-types'
import {
  trackFreeTrialStarted,
  trackStudioAiFreeTrialStarted,
  trackMonthlySubscriptionStarted,
  trackYearlySubscriptionStarted,
  trackStudioAiYearlySubscriptionStarted,
  trackCoinPackMonthly25kSubscriptionStarted,
  trackCoinPackMonthly100kSubscriptionStarted,
  trackCoinPackMonthly250kSubscriptionStarted,
  trackCoinPackMonthly400kSubscriptionStarted,
  trackCoinPackMonthly700kSubscriptionStarted,
  trackCoinPackMonthly1MSubscriptionStarted,
} from '@repo/analytics/google-site-tag'
import {
  CF_ALL_ACCESS_YEARLY_PRICE,
  CF_ALL_ACCESS_MONTHLY_PRICE_USD,
  CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
  CF_TEAM_MONTHLY_PRICE,
} from 'constants/prices'
import {
  CF_ALL_ACCESS_MONTHLY_PRODUCT_ID,
  CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
} from 'constants/product-ids'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'

const GOOGLE_ADS_PIXEL_SUBSCRIPTION =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_PIXEL_SUBSCRIPTION ||
  process.env.VITE_GOOGLE_ADS_PIXEL_SUBSCRIPTION

const GOOGLE_ADS_TAG = process.env.NEXT_PUBLIC_GOOGLE_ADS_TAG || process.env.VITE_GOOGLE_ADS_TAG

export type ValidSubscriptionType =
  | SubscriptionType.AllAccessMonthly
  | SubscriptionType.AllAccessYearly
  | SubscriptionType.AllAccessYearlyPromo
  | SubscriptionType.AllAccessMonthlyFreeTrial
  | SubscriptionType.AllAccessYearlyFreeTrial
  | SubscriptionType.StudioAiYearly
  | SubscriptionType.StudioAiYearlyFreeTrial
  | SubscriptionType.CoinPackMonthly25k
  | SubscriptionType.CoinPackMonthly100k
  | SubscriptionType.CoinPackMonthly250k
  | SubscriptionType.CoinPackMonthly400k
  | SubscriptionType.CoinPackMonthly700k
  | SubscriptionType.CoinPackMonthly1M
  | TeamSubscriptionType

interface SubscriptionOrderAnalytics {
  /** Google Ads conversion event value */
  adsConversionValue: string | number
  itemId: string | number
  gtagItemName: string
  shortName: string
  amplitudeType: string
  trackSubscriptionStarted?: () => void
}

const subscriptionOrderAnalyticsByType: Record<ValidSubscriptionType, SubscriptionOrderAnalytics> =
  {
    [SubscriptionType.AllAccessMonthly]: {
      adsConversionValue: CF_ALL_ACCESS_MONTHLY_PRICE_USD,
      itemId: CF_ALL_ACCESS_MONTHLY_PRODUCT_ID,
      gtagItemName: 'Subscription Plan - All Access',
      shortName: 'All Access',
      amplitudeType: 'all_access',
      trackSubscriptionStarted: trackMonthlySubscriptionStarted,
    },
    [SubscriptionType.AllAccessYearly]: {
      adsConversionValue: CF_ALL_ACCESS_YEARLY_PRICE,
      itemId: CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
      gtagItemName: 'Subscription Plan - All Access',
      shortName: 'All Access',
      amplitudeType: 'all_access',
      trackSubscriptionStarted: trackYearlySubscriptionStarted,
    },
    [SubscriptionType.AllAccessYearlyPromo]: {
      adsConversionValue: CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
      itemId: CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
      gtagItemName: 'Subscription Plan - All Access',
      shortName: 'All Access',
      amplitudeType: 'all_access',
      trackSubscriptionStarted: trackYearlySubscriptionStarted,
    },
    [SubscriptionType.AllAccessMonthlyFreeTrial]: {
      adsConversionValue: 0,
      itemId: CF_ALL_ACCESS_MONTHLY_PRODUCT_ID,
      gtagItemName: 'Subscription Plan - All Access',
      shortName: 'All Access',
      amplitudeType: 'all_access_trial',
    },
    [SubscriptionType.AllAccessYearlyFreeTrial]: {
      adsConversionValue: 0,
      itemId: CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
      gtagItemName: 'Subscription Plan - All Access',
      shortName: 'All Access',
      amplitudeType: 'all_access_trial',
    },
    [SubscriptionType.StudioAiYearly]: {
      adsConversionValue: CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
      itemId: 'studio_ai_yearly',
      gtagItemName: 'Subscription Plan - Studio AI',
      shortName: 'Studio AI',
      amplitudeType: 'studio_ai',
      trackSubscriptionStarted: trackStudioAiYearlySubscriptionStarted,
    },
    [SubscriptionType.StudioAiYearlyFreeTrial]: {
      adsConversionValue: 0,
      itemId: 'studio_ai_yearly_free_trial',
      gtagItemName: 'Subscription Plan - Studio AI',
      shortName: 'Studio AI',
      amplitudeType: 'studio_ai_trial',
    },
    [SubscriptionType.CoinPackMonthly25k]: {
      adsConversionValue: 3.99,
      itemId: 'coin_pack_monthly_25k',
      gtagItemName: 'Subscription Plan - Coin Pack Monthly 25k ',
      shortName: 'Coin Pack Monthly 25k',
      amplitudeType: 'coin_pack_monthly_25k',
      trackSubscriptionStarted: trackCoinPackMonthly25kSubscriptionStarted,
    },
    [SubscriptionType.CoinPackMonthly100k]: {
      adsConversionValue: 9.99,
      itemId: 'coin_pack_monthly_100k',
      gtagItemName: 'Subscription Plan - Coin Pack Monthly 100k',
      shortName: 'Coin Pack Monthly 100k',
      amplitudeType: 'coin_pack_monthly_100k',
      trackSubscriptionStarted: trackCoinPackMonthly100kSubscriptionStarted,
    },
    [SubscriptionType.CoinPackMonthly250k]: {
      adsConversionValue: 19.99,
      itemId: 'coin_pack_monthly_250k',
      gtagItemName: 'Subscription Plan - Coin Pack Monthly 250k',
      shortName: 'Coin Pack Monthly 250k',
      amplitudeType: 'coin_pack_monthly_250k',
      trackSubscriptionStarted: trackCoinPackMonthly250kSubscriptionStarted,
    },
    [SubscriptionType.CoinPackMonthly400k]: {
      adsConversionValue: 29.99,
      itemId: 'coin_pack_monthly_400k',
      gtagItemName: 'Subscription Plan - Coin Pack Monthly 400k',
      shortName: 'Coin Pack Monthly 400k',
      amplitudeType: 'coin_pack_monthly_400k',
      trackSubscriptionStarted: trackCoinPackMonthly400kSubscriptionStarted,
    },
    [SubscriptionType.CoinPackMonthly700k]: {
      adsConversionValue: 49.99,
      itemId: 'coin_pack_monthly_700k',
      gtagItemName: 'Subscription Plan - Coin Pack Monthly 700k',
      shortName: 'Coin Pack Monthly 700k',
      amplitudeType: 'coin_pack_monthly_700k',
      trackSubscriptionStarted: trackCoinPackMonthly700kSubscriptionStarted,
    },
    [SubscriptionType.CoinPackMonthly1M]: {
      adsConversionValue: 69.99,
      itemId: 'coin_pack_monthly_1m',
      gtagItemName: 'Subscription Plan - Coin Pack Monthly 1M',
      shortName: 'Coin Pack Monthly 1M',
      amplitudeType: 'coin_pack_monthly_1m',
      trackSubscriptionStarted: trackCoinPackMonthly1MSubscriptionStarted,
    },
    [TEAM_MONTHLY_SUBSCRIPTION_TYPE]: {
      adsConversionValue: CF_TEAM_MONTHLY_PRICE,
      itemId: 'team_monthly',
      gtagItemName: 'Subscription Plan - Teams',
      shortName: 'Teams',
      amplitudeType: 'team',
    },
  }

const validSubscriptionTypeKeys = Object.keys(
  subscriptionOrderAnalyticsByType,
) as ValidSubscriptionType[]

export function isValidSubscriptionType(
  subscriptionType: string | null | undefined,
): subscriptionType is ValidSubscriptionType {
  if (subscriptionType === null || subscriptionType === undefined) {
    return false
  }
  return validSubscriptionTypeKeys.includes(subscriptionType as ValidSubscriptionType)
}

export interface AnalyticsOrderSuccessOptions {
  id: string
  tax: number
  total: number
  userId: string
  subscriptionType: ValidSubscriptionType
  currency: string
}

const isLive = process.env.NEXT_PUBLIC_ENV || process.env.VITE_PUBLIC_ENV

export function useAnalyticsOrderSuccess() {
  function onSuccessForAnalytics(options: AnalyticsOrderSuccessOptions) {
    const analytics = subscriptionOrderAnalyticsByType[options.subscriptionType]
    const gtag =
      typeof window !== 'undefined' && typeof window.gtag === 'function' ? window.gtag : undefined

    const subtotalCents = options.total - options.tax
    const isFreeTrial = subtotalCents === 0
    const subtotalCurrencyUnits = isFreeTrial ? 0 : subtotalCents / 100

    if (gtag && isLive) {
      gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_TAG}/${GOOGLE_ADS_PIXEL_SUBSCRIPTION}`,
        value: analytics.adsConversionValue,
        currency: 'USD',
      })

      const item = {
        item_id: analytics.itemId,
        item_name: analytics.gtagItemName,
        price: subtotalCurrencyUnits,
        quantity: 1,
        item_category1: 'Subscriptions',
        item_category2: [analytics.shortName],
        ...(isFreeTrial
          ? {
              item_variant:
                options.subscriptionType === SubscriptionType.StudioAiYearlyFreeTrial
                  ? 'Studio AI Free Trial'
                  : 'All Access Free Trial',
            }
          : {}),
      }

      const order = {
        currency: options.currency.toUpperCase(),
        transaction_id: options.id,
        value: subtotalCurrencyUnits,
        tax: options.tax === 0 ? options.tax : options.tax / 100,
        items: [item],
      }

      gtag('event', 'purchase', order)
    }

    if (gtag) {
      if (isFreeTrial) {
        if (options.subscriptionType === SubscriptionType.StudioAiYearlyFreeTrial) {
          trackStudioAiFreeTrialStarted()
        } else {
          trackFreeTrialStarted()
        }
      }

      analytics.trackSubscriptionStarted?.()
    }

    // sadly no types for bing :(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const uetq = (window as any).uetq

    if (uetq) {
      const transactionId = options.id

      if (transactionId) {
        const ecommProdId = analytics.itemId

        uetq.push('event', 'purchase', {
          transaction_id: transactionId,
          ecomm_prodid: [ecommProdId],
          ecomm_pagetype: 'purchase',
          ecomm_totalvalue: subtotalCurrencyUnits,
          revenue_value: subtotalCurrencyUnits,
          currency: options.currency.toUpperCase(),
          items: [{ id: ecommProdId, quantity: 1, price: subtotalCurrencyUnits }],
        })

        uetq.push('event', 'started', {
          event_category: 'subscription',
          revenue_value: subtotalCurrencyUnits,
          currency: options.currency.toUpperCase(),
        })
      }
    }

    // no types for fbq :(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fbq = (window as any).fbq

    if (typeof fbq === 'function') {
      fbq('track', 'Subscribe', {
        value: String(analytics.adsConversionValue),
        currency: 'USD',
      })
    }

    sendAmplitudeEvent(AmplitudeEvents.WELCOME_POPUP_VIEWED, {
      type: analytics.amplitudeType,
    })
  }

  return { onSuccessForAnalytics: React.useCallback(onSuccessForAnalytics, []) }
}
